import { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Avatar,
  Stack,
  Typography,
  CircularProgress,
  Alert,
  InputAdornment,
  Pagination,
  TableSortLabel,
} from "@mui/material";
import useCoreProducts from "../../services/useCoreProducts";
import useCoreProductCategories from "../../services/useCoreProductCategories";
import useFarmers from "../../services/useFarmers";
import useCreateProductOffering from "../../services/useCreateProductOffering";
import { IProductCore, IProductOffering } from "../../types/products";

interface ProductsSelectorProps {
  fairId: number;
  existingOfferings: IProductOffering[];
  onClose: () => void;
  onSuccess: (count: number) => void;
}

interface ProductSelection {
  productId: number;
  selected: boolean;
  price: string;
  cost: string;
  stock: number;
  unlimited_stock: boolean;
  available: boolean;
}

const PAGE_SIZE = 20;
type SortColumn = "product" | "producer";
type SortDirection = "asc" | "desc";

/**
 * Componente para selecionar produtos do catálogo e adicioná-los à feira
 */
const ProductsSelector = ({
  fairId,
  existingOfferings,
  onClose,
  onSuccess,
}: ProductsSelectorProps) => {
  const [categoryFilter, setCategoryFilter] = useState<number | null>(null);
  const [producerFilter, setProducerFilter] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<SortColumn>("product");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [selections, setSelections] = useState<
    Record<number, ProductSelection>
  >({});
  const [submitting, setSubmitting] = useState(false);

  const orderingParam = `${sortDirection === "desc" ? "-" : ""}${sortColumn}`;

  const filters = {
    active: true, // Apenas produtos ativos
    category: categoryFilter || undefined,
    producer: producerFilter || undefined,
    search: searchTerm,
    ordering: orderingParam,
    page,
    excludeOfferedFair: fairId,
  };

  const { data: productsResponse, isLoading } = useCoreProducts(filters);
  const { data: categories } = useCoreProductCategories();
  const { data: farmers } = useFarmers();
  const createMutation = useCreateProductOffering();

  // Contagem de produtos selecionados
  const selectedCount = Object.values(selections).filter(
    (s) => s.selected,
  ).length;

  const normalizeMoney = (value?: string | null) =>
    value === null || value === undefined ? "" : String(value);

  const initializeSelection = (product: IProductCore): ProductSelection => ({
    productId: product.id,
    selected: false,
    price: normalizeMoney(product.last_offering_price),
    cost: normalizeMoney(product.last_offering_cost),
    stock: 0,
    unlimited_stock: false,
    available: true,
  });

  const getSelection = (productId: number): ProductSelection => {
    if (!selections[productId]) {
      const product = availableProducts.find((p) => p.id === productId);
      if (product) {
        const initial = initializeSelection(product);
        setSelections((prev) => ({ ...prev, [productId]: initial }));
        return initial;
      }
    }
    return selections[productId];
  };

  const updateSelection = (
    productId: number,
    updates: Partial<ProductSelection>,
  ) => {
    setSelections((prev) => ({
      ...prev,
      [productId]: { ...getSelection(productId), ...updates },
    }));
  };

  const toggleSelection = (productId: number) => {
    const current = getSelection(productId);
    updateSelection(productId, { selected: !current.selected });
  };

  const handleSelectAll = () => {
    const allSelected = availableProducts.every(
      (p) => getSelection(p.id).selected,
    );

    const updates: Record<number, ProductSelection> = {};
    availableProducts.forEach((product) => {
      const current = getSelection(product.id);
      updates[product.id] = { ...current, selected: !allSelected };
    });

    setSelections((prev) => ({ ...prev, ...updates }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    const selectedProducts = Object.values(selections).filter(
      (s) => s.selected,
    );

    if (selectedProducts.length === 0) {
      onClose();
      return;
    }

    // Validar todos os produtos selecionados
    for (const selection of selectedProducts) {
      const price = parseFloat(selection.price);
      const cost = parseFloat(selection.cost);

      if (isNaN(price) || price <= 0) {
        alert("Todos os produtos devem ter preço válido (maior que zero)");
        setSubmitting(false);
        return;
      }

      if (isNaN(cost) || cost < 0) {
        alert("Custo não pode ser negativo");
        setSubmitting(false);
        return;
      }

      if (cost > price) {
        alert("Custo não pode ser maior que o preço de venda");
        setSubmitting(false);
        return;
      }
    }

    // Criar ofertas em sequência
    let successCount = 0;
    let hasError = false;

    for (const selection of selectedProducts) {
      try {
        await createMutation.mutateAsync({
          fairId,
          offering: {
            product: selection.productId,
            fair: fairId,
            price: selection.price,
            cost: selection.cost,
            stock: selection.stock,
            unlimited_stock: selection.unlimited_stock,
            available: selection.available,
          },
        });
        successCount++;
      } catch (error) {
        console.error("Erro ao criar oferta:", error);
        hasError = true;
        break;
      }
    }

    setSubmitting(false);

    if (hasError) {
      alert(
        `${successCount} produto(s) adicionado(s) com sucesso, mas houve um erro com os demais.`,
      );
    }

    if (successCount > 0) {
      onSuccess(successCount);
    } else {
      onClose();
    }
  };

  const availableProducts = productsResponse?.results || [];
  const allSelected =
    availableProducts.length > 0 &&
    availableProducts.every((p) => getSelection(p.id).selected);

  useEffect(() => {
    setPage(1);
  }, [categoryFilter, producerFilter, searchTerm]);

  useEffect(() => {
    if (!productsResponse) return;
    const totalPages = Math.max(
      1,
      Math.ceil(productsResponse.count / PAGE_SIZE),
    );
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [productsResponse, page]);

  const handleSort = (column: SortColumn) => {
    setPage(1);
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortColumn(column);
    setSortDirection("asc");
  };

  return (
    <Box sx={{ minHeight: 400 }}>
      {/* Filtros */}
      <Stack spacing={2} mb={3} mt={2}>
        <TextField
          fullWidth
          label="Buscar por nome do produto ou produtor"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
        />

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Categoria</InputLabel>
            <Select
              value={categoryFilter?.toString() || ""}
              onChange={(e: SelectChangeEvent) =>
                setCategoryFilter(
                  e.target.value ? Number(e.target.value) : null,
                )
              }
              label="Categoria"
            >
              <MenuItem value="">Todas</MenuItem>
              {categories?.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Produtor</InputLabel>
            <Select
              value={producerFilter?.toString() || ""}
              onChange={(e: SelectChangeEvent) =>
                setProducerFilter(
                  e.target.value ? Number(e.target.value) : null,
                )
              }
              label="Produtor"
            >
              <MenuItem value="">Todos</MenuItem>
              {farmers?.map((farmer) => (
                <MenuItem key={farmer.id} value={farmer.id}>
                  {farmer.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Stack>

      {/* Informações */}
      {selectedCount > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {selectedCount} produto{selectedCount !== 1 ? "s" : ""} selecionado
          {selectedCount !== 1 ? "s" : ""}. Configure preço, custo e estoque na
          tabela abaixo.
        </Alert>
      )}

      {/* Lista de produtos */}
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : productsResponse?.results.length === 0 ? (
        <Alert severity="info">
          Nenhum produto encontrado com os filtros aplicados.
        </Alert>
      ) : (
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    onChange={handleSelectAll}
                    disabled={submitting}
                  />
                </TableCell>
                <TableCell
                  sortDirection={
                    sortColumn === "product" ? sortDirection : false
                  }
                >
                  <TableSortLabel
                    active={sortColumn === "product"}
                    direction={sortColumn === "product" ? sortDirection : "asc"}
                    onClick={() => handleSort("product")}
                  >
                    Produto
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={
                    sortColumn === "producer" ? sortDirection : false
                  }
                >
                  <TableSortLabel
                    active={sortColumn === "producer"}
                    direction={
                      sortColumn === "producer" ? sortDirection : "asc"
                    }
                    onClick={() => handleSort("producer")}
                  >
                    Produtor
                  </TableSortLabel>
                </TableCell>
                <TableCell width={120}>Preço</TableCell>
                <TableCell width={120}>Custo</TableCell>
                <TableCell width={90}>Estoque</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {availableProducts.map((product) => {
                const selection = getSelection(product.id);

                return (
                  <TableRow
                    key={product.id}
                    hover
                    selected={selection.selected}
                    sx={{ opacity: selection.selected ? 1 : 0.6 }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selection.selected}
                        onChange={() => toggleSelection(product.id)}
                        disabled={submitting}
                      />
                    </TableCell>

                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar
                          src={product.image}
                          alt={product.name}
                          variant="rounded"
                          sx={{ width: 32, height: 32 }}
                        >
                          {product.name.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2">
                            {product.name}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {product.producer.name}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={selection.price}
                        onChange={(e) =>
                          updateSelection(product.id, { price: e.target.value })
                        }
                        disabled={!selection.selected || submitting}
                        inputProps={{ step: "0.01", min: "0" }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">R$</InputAdornment>
                          ),
                        }}
                        sx={{ width: 120 }}
                      />
                    </TableCell>

                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={selection.cost}
                        onChange={(e) =>
                          updateSelection(product.id, { cost: e.target.value })
                        }
                        disabled={!selection.selected || submitting}
                        inputProps={{ step: "0.01", min: "0" }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">R$</InputAdornment>
                          ),
                        }}
                        sx={{ width: 120 }}
                      />
                    </TableCell>

                    <TableCell align="center">
                      <Stack spacing={0.5} alignItems="center">
                        <TextField
                          size="small"
                          type="number"
                          value={selection.stock}
                          onChange={(e) =>
                            updateSelection(product.id, {
                              stock: parseInt(e.target.value) || 0,
                            })
                          }
                          disabled={
                            !selection.selected ||
                            selection.unlimited_stock ||
                            submitting
                          }
                          inputProps={{ min: "0" }}
                          sx={{ width: 90 }}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            fontSize: "0.7rem",
                          }}
                        >
                          <Checkbox
                            size="small"
                            checked={selection.unlimited_stock}
                            onChange={(e) =>
                              updateSelection(product.id, {
                                unlimited_stock: e.target.checked,
                              })
                            }
                            disabled={!selection.selected || submitting}
                          />
                          Ilimitado
                        </Box>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {productsResponse && productsResponse.count > PAGE_SIZE && (
        <Stack alignItems="center" mt={2}>
          <Pagination
            color="primary"
            page={page}
            count={Math.max(1, Math.ceil(productsResponse.count / PAGE_SIZE))}
            onChange={(_, newPage) => setPage(newPage)}
          />
        </Stack>
      )}

      {/* Ações */}
      <Stack
        direction="row"
        spacing={2}
        justifyContent="flex-end"
        sx={{ mt: 3 }}
      >
        <Button onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={selectedCount === 0 || submitting}
        >
          {submitting
            ? "Adicionando..."
            : `Adicionar ${selectedCount} produto${selectedCount !== 1 ? "s" : ""}`}
        </Button>
      </Stack>
    </Box>
  );
};

export default ProductsSelector;
